import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getPricingTreatments, hasCustomPricingData, getTranslatedName } from '@/lib/treatmentStore';

const dentalSlugs: Record<string, string> = {
  'Zahnimplantat': 'dental-implant',
  'Zirkonkrone': 'zirconia-crown',
  'Keramikkrone': 'ceramic-crown',
  'Unsichtbare Zahnspange': 'invisible-braces',
  'Professionelle Zahnreinigung': 'dental-cleaning',
  'Zahnaufhellung': 'teeth-whitening',
  'Kleiner Knochenaufbau': 'small-bone-graft',
  'Großer Knochenaufbau': 'large-bone-graft',
  'Sinus Lift': 'sinus-lift',
  'Pterygoid-Implantat': 'pterygoid-implant',
  'Zygomatisches Implantat': 'zygomatic-implant',
  'Elastische Prothese': 'elastic-denture',
};

type Category = {
  key: string;
  icon: string;
  names: string[];
};

const CATEGORIES: Category[] = [
  { key: 'implants', icon: 'ri-tooth-line', names: ['Zahnimplantat', 'Pterygoid-Implantat', 'Zygomatisches Implantat'] },
  { key: 'crowns', icon: 'ri-vip-crown-line', names: ['Zirkonkrone', 'Keramikkrone'] },
  { key: 'aesthetics', icon: 'ri-sparkling-line', names: ['Unsichtbare Zahnspange', 'Professionelle Zahnreinigung', 'Zahnaufhellung'] },
  { key: 'bone', icon: 'ri-surgical-mask-line', names: ['Kleiner Knochenaufbau', 'Großer Knochenaufbau', 'Sinus Lift'] },
  { key: 'prosthetics', icon: 'ri-emotion-happy-line', names: ['Elastische Prothese'] },
];

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  implants: 'svc.dental_cat_implants_label',
  crowns: 'svc.dental_cat_crowns_label',
  aesthetics: 'svc.dental_cat_aesthetics_label',
  bone: 'svc.dental_cat_bone_label',
  prosthetics: 'svc.dental_cat_prosthetics_label',
};

const CATEGORY_DESC_KEYS: Record<string, string> = {
  implants: 'svc.dental_cat_implants_desc',
  crowns: 'svc.dental_cat_crowns_desc',
  aesthetics: 'svc.dental_cat_aesthetics_desc',
  bone: 'svc.dental_cat_bone_desc',
  prosthetics: 'svc.dental_cat_prosthetics_desc',
};

// Meta stored as i18n tokens. `duration` is the numeric range; the unit is
// rendered via t('pmeta.min'). `anesthesia` and `sessions` are pmeta keys.
const TREATMENT_META: Record<string, { duration: string; anesthesia: string; sessions: string }> = {
  'Zahnimplantat': { duration: '60–90', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_2' },
  'Zirkonkrone': { duration: '45–60', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_2' },
  'Keramikkrone': { duration: '45–60', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_2' },
  'Unsichtbare Zahnspange': { duration: '30', anesthesia: 'pmeta.anes_none', sessions: 'pmeta.sessions_ongoing' },
  'Professionelle Zahnreinigung': { duration: '45–60', anesthesia: 'pmeta.anes_none', sessions: 'pmeta.sessions_1' },
  'Zahnaufhellung': { duration: '60–90', anesthesia: 'pmeta.anes_none', sessions: 'pmeta.sessions_1_2' },
  'Kleiner Knochenaufbau': { duration: '30–45', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_1' },
  'Großer Knochenaufbau': { duration: '60–90', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_1' },
  'Sinus Lift': { duration: '45–60', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_1' },
  'Pterygoid-Implantat': { duration: '45–60', anesthesia: 'pmeta.anes_local', sessions: 'pmeta.sessions_1' },
  'Zygomatisches Implantat': { duration: '60–90', anesthesia: 'pmeta.anes_general', sessions: 'pmeta.sessions_1' },
  'Elastische Prothese': { duration: '30–45', anesthesia: 'pmeta.anes_none', sessions: 'pmeta.sessions_2_3' },
};

interface TreatmentItem {
  name: string;
  price: string;
  stay?: string;
}

function getTreatments(lang: string): TreatmentItem[] {
  return getPricingTreatments('dental', lang);
}

function getSlugByName(name: string): string | undefined {
  return dentalSlugs[name];
}

export default function DentalPriceTable() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const items = getTreatments(lang);
  const isCustom = hasCustomPricingData();

  const getItemByGermanName = (deName: string): TreatmentItem | null => {
    const translated = getTranslatedName(deName, lang);
    return items.find((item) => item.name === translated) || null;
  };

  // Resolve the German key for an item whose name may be translated, so the
  // German-keyed TREATMENT_META and dentalSlugs maps still match.
  const germanNameOf = (name: string): string => {
    if (TREATMENT_META[name] || dentalSlugs[name]) return name;
    const de = Object.keys(dentalSlugs).find((k) => getTranslatedName(k, lang) === name);
    return de || name;
  };

  const renderTreatmentCard = (item: TreatmentItem, idx: number) => {
    const deName = germanNameOf(item.name);
    const slug = getSlugByName(deName);
    const meta = TREATMENT_META[deName];

    return (
      <div
        key={idx}
        className="group p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:shadow-sm transition-all duration-300"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-label text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors">
              {item.name}
            </h4>
            {meta && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400 font-body">
                  <i className="ri-time-line text-[10px]"></i>
                  {`${meta.duration} ${t('pmeta.min')}`}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400 font-body">
                  <i className="ri-psychotherapy-line text-[10px]"></i>
                  {t(meta.anesthesia)}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400 font-body">
                  <i className="ri-calendar-line text-[10px]"></i>
                  {t(meta.sessions)}
                </span>
              </div>
            )}
            {!meta && item.stay && (
              <p className="text-[11px] text-foreground-400 font-body mt-1.5 flex items-center gap-1">
                <i className="ri-hotel-bed-line text-[10px]"></i>
                {item.stay}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-heading text-lg font-bold text-primary-600 whitespace-nowrap">
              {item.price} €
            </p>
            <p className="text-[10px] text-foreground-400 font-body mt-0.5">
              {item.price.includes('–') ? t('svc.price_from') : t('svc.price_fixed')}
            </p>
          </div>
        </div>
        {slug && (
          <div className="mt-3 pt-3 border-t border-background-100">
            <Link
              to={`/services/${slug}`}
              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-label transition-colors cursor-pointer no-underline"
            >
              {t('svc.view_details_link')}
              <i className="ri-arrow-right-line text-[10px]"></i>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <section id="dental" className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] md:text-xs tracking-[0.15em] uppercase font-label">
            <i className="ri-tooth-line text-xs"></i>
            {t('pricing.dental_title')}
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900">
            {t('pricing.dental_title')}
          </h2>
          <p className="mt-3 text-sm text-foreground-500 font-body max-w-lg mx-auto leading-relaxed break-words">
            {t('svc.dental_subtitle')}
          </p>
        </div>

        {isCustom ? (
          /* Flat list for custom data — no category grouping since order may differ */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((item, idx) => renderTreatmentCard(item, idx))}
          </div>
        ) : (
          /* Categorized view for default data */
          <div className="space-y-10">
            {CATEGORIES.map((cat) => {
              const catItems = cat.names
                .map(getItemByGermanName)
                .filter((item): item is NonNullable<typeof item> => item !== null);

              if (catItems.length === 0) return null;

              return (
                <div key={cat.key}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className={`${cat.icon} text-lg text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-label text-base font-semibold text-foreground-900">
                        {CATEGORY_LABEL_KEYS[cat.key] ? t(CATEGORY_LABEL_KEYS[cat.key]) : cat.key}
                      </h3>
                      <p className="text-xs text-foreground-500 font-body mt-0.5">
                        {CATEGORY_DESC_KEYS[cat.key] ? t(CATEGORY_DESC_KEYS[cat.key]) : ''}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {catItems.map((item, idx) => renderTreatmentCard(item, idx))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground-400 font-body">
          {t('pricing.note_individual')}
        </p>
      </div>
    </section>
  );
}